import React from 'react';
import { useTheme } from '@mui/material/styles';

/**
 * Waypoint wordmark.
 *
 * Static (no animation). Uses the RJA brand purple->yellow vertical gradient
 * (#CA8ACC top -> #F4B97A bottom), matching `public/rja-logo-text.svg`. The
 * same colors are used in both light and dark mode (the RJA logo does not
 * theme). A faded, slightly-offset duplicate of the wordmark renders BEHIND
 * the crisp main wordmark to give the flat mark a subtle 3D / depth "echo".
 */
const WaypointLogo: React.FC<{ height?: number }> = ({ height = 32 }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // RJA brand gradient anchors: purple (top) -> warm yellow/peach (bottom).
  // Identical in both modes to match the (non-theming) RJA logo.
  const gradStart = '#CA8ACC';
  const gradEnd = '#F4B97A';

  // Slightly darker/desaturated variant for the faded background double so the
  // echo reads as a shadow behind the crisp main wordmark.
  const echoStart = '#9A6A9C';
  const echoEnd = '#C99258';

  // The depth echo is a touch more visible on dark backgrounds.
  const echoOpacity = isDark ? 0.3 : 0.22;

  const fontProps = {
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: 36,
    fontWeight: 700,
    letterSpacing: '-0.5',
  } as const;

  return (
    <svg
      // Wordmark occupies y ~0..40; small extra width/height padding leaves
      // room for the offset background double without a large empty gap.
      viewBox="0 0 174 44"
      role="img"
      aria-label="Waypoint"
      style={{ height, width: 'auto', overflow: 'visible', display: 'block' }}
    >
      <defs>
        {/* RJA brand vertical gradient: purple -> yellow. */}
        <linearGradient id="wp-base" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={gradStart} />
          <stop offset="100%" stopColor={gradEnd} />
        </linearGradient>

        {/* Darker/desaturated gradient for the faded depth echo behind. */}
        <linearGradient id="wp-echo" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={echoStart} />
          <stop offset="100%" stopColor={echoEnd} />
        </linearGradient>
      </defs>

      {/* Faded background double — offset down-and-right, reduced opacity, to
          give the flat wordmark a static 3D depth "echo". Rendered first so it
          sits behind the crisp main wordmark. */}
      <text x="3" y="39" {...fontProps} fill="url(#wp-echo)" opacity={echoOpacity}>
        Waypoint
      </text>

      {/* Main wordmark — RJA purple->yellow gradient, full opacity, on top. */}
      <text x="0" y="36" {...fontProps} fill="url(#wp-base)">
        Waypoint
      </text>
    </svg>
  );
};

export default WaypointLogo;
